// include all locales even though we may not officially have all languages
// translated since some people can read english but may still prefer to show
// units for their locale.
// Note: despite being the path referencing the 'min' folder, this location IS
// this non-minimized file containing all locales
import 'moment/min/locales'

// it seems you need to load the locales before loading moment itself
import moment from 'moment-timezone'

import features from './features'

// see http://momentjs.com/docs/#/i18n/
import Backbone from 'backbone'

import _ from 'lodash'

const numericDateFormat = 'l' // e.g. 9/30/1986
const dateFormat = 'll' // e.g. Sep 30 1986
const defaultTimeFormat = 'LTS' // e.g. 8:52:23 PM
const isoDateFormat = 'YYYY-MM-DD'
import layoutManager from 'scoreboard/components/layout-manager'
import config from 'scoreboard/components/config'

const hr24timeFormat = 'HH:mm:ss'
// #35379: A zone-agnostic format, intended to be used wherever a datetime is
// deserialized to a Java LocalDateTime - which will ignore zone indicators, and
// therefore should not be handed a datetime that has had a zone offset
// applied.
const serverLocalDateTimeFormat = `${isoDateFormat}[T]${hr24timeFormat}`

/**
 * Exports are performed from a Chromium browser running on an export server somewhere.
 * The browser timezone will be that of the export server, which may not be congruent with
 * that user's timezone.  So if the user had a timeZoneOffset user preference we apply that
 * in favor of whatever the browser is using.
 *
 * (The preference is updated on each login)
 *
 * @param date
 * @return {Moment} a Moment instance for the provided date, with the utcOffset applied
 */
let utcOffset
Backbone.on('load', ({user = {}}) => {
	const {timeZoneOffset} = user
	if (timeZoneOffset) {
		// If data is reloaded ever (scoreboard-db-wizard), don't wipe out the offset
		// unless a new one is provided (#22185)
		utcOffset = timeZoneOffset
	}
})

const defaultTimeZone = function() {
	return Intl.DateTimeFormat().resolvedOptions().timeZone
}

const zonedMoment = (date, format) => {
	// only apply an offset to UTC Zoned date strings
	return utcOffset && _.endsWith(date, 'Z') ?
		moment(date, format).utcOffset(utcOffset) : moment(date, format)
}

const parseIsoTime = (timeStr) => {
	return zonedMoment(timeStr, [hr24timeFormat])
}

function getFormattedDateTime(dateTime, zone, format) {
	return moment.tz(dateTime, undefined, zone)
		.tz(defaultTimeZone())
		.format(format)
}

/**
 * Provides handlebars helpers for working with the moment library.  You can
 * see how these are configured in setup.js
 */
var momentHelper = {

	/**
	 * Get the timezone offset for the current browser, typically
	 * comes from the system timezone.
	 *
	 * @return {number} negative value if behind UTC, positive it ahead of UTC
	 */
	readTimeZoneOffset() {
		/**
		 * From MDN: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getTimezoneOffset#Description
		 *
		 * The time-zone offset is the difference, in minutes, from local time to UTC.
		 *
		 * This means that the offset is positive if the local timezone is behind UTC
		 * and negative if it is ahead.
		 *
		 * So we have to invert it for dates to show up correctly
		 */
		return new Date().getTimezoneOffset() * -1
	},

	/**
	 * Generate pretty relative time references like "5 [sec/min/days] ago". This
	 * measures time passed from the current date and time which means more precise
	 * time spans can be returned (seconds, minutes, etc.).
	 *
	 * Example: (current day/time: Apr 12 2018 @ 12pm)
	 * var date = new Date('Thu Apr 12 2018 1:00:00 GMT-0400 (EDT)')
	 * momentHelper.fromNow(date)
	 * // returns "11 hours ago"
	 *
	 * @param withoutSuffix omit 'ago'
	 */
	fromNow: function(date, withoutSuffix) {
		if (!date) {
			return ''
		}
		// If an export, show the actual date
		if (layoutManager.export) {
			return this.formatDate(date)
		}
		return zonedMoment(date).fromNow(withoutSuffix === true)
	},

	/**
	 * Generate pretty relative time references like "5 [days/months/years] ago".
	 * This measures time passed from the start of the current day and should be used
	 * when measuring a time span of at least a day. If measuring a time span of
	 * less than a day, use fromNow() instead.
	 *
	 * Example: (current day/time: Apr 12 2018 @ 12pm)
	 * var date = new Date('Thu Apr 9 2018 1:00:00 GMT-0400 (EDT)')
	 * momentHelper.fromToday(date)
	 * // returns "3 days ago"
	 */
	fromToday: function(date) {
		return this.fromDate(moment(), date)
	},

	/**
	 * Similar to fromToday(), but the first date is a reference point (instead of today)
	 */
	fromDate: function(referenceDate, date) {
		if (!date) {
			return null
		}

		// If an export, show the actual date
		if (layoutManager.export) {
			return this.formatDate(date)
		}
		return zonedMoment(date).from(moment(referenceDate).startOf('day'))
	},

	/**
	 * Generate pretty relative time differences like "5 [sec/min/days]".
	 * This measures time between a "reference time" (like a creation date) and
	 * another time (like a modification date).
	 */
	difference(referenceDate, date) {
		if (!referenceDate || !date) {
			return ''
		}

		return zonedMoment(date).from(moment(referenceDate), true)
	},

	/**
	 * Formats a date for display for a user, e.g. Sep 4 1986
	 */
	formatDate: function(date) {
		if (!date) {
			return null
		}
		return zonedMoment(date).format(dateFormat)
	},

	/**
	 * Formats a date for editing by a user, e.g. 9/4/1986
	 */
	formatNumericDate: function(date) {
		if (!date) {
			return null
		}
		return zonedMoment(date).format(numericDateFormat)
	},

	/**
	 * NOTE: TimeDisplayType.kt should be kept in sync
	 * @typedef {'hour-24'|'hour-24-seconds'|'hour-12'|'hour-12-seconds'} TimeDisplayType
	 */

	/**
	 * @param {TimeDisplayType} timeDisplayType
	 * Is the given display type a 24hr type?
	 */
	is24hrTimeType(timeDisplayType = config.defaultTimeDisplayType) {
		return _.includes(['hour-24', 'hour-24-seconds'], timeDisplayType)
	},

	/**
	 * @param {TimeDisplayType} timeDisplayType
	 * Does the given type include seconds?
	 */
	isSecondsTimeType(timeDisplayType = config.defaultTimeDisplayType) {
		return _.includes(['hour-24-seconds', 'hour-12-seconds'], timeDisplayType)
	},

	/**
	 * Returns the actual format string for a time format type or the application
	 * default if no type is provided
	 */
	getTimeFormat(displayType) {
		switch (displayType || config.defaultTimeDisplayType) {
			case 'hour-12': return 'LT'
			case 'hour-24': return 'HH:mm'
			case 'hour-12-seconds': return 'LTS'
			case 'hour-24-seconds': return hr24timeFormat
			default: return defaultTimeFormat
		}
	},

	/**
	 * Formats a time for display for a user, e.g. 8:30 PM
	 */
	formatTime: function(dateTime, paramFormat) {
		if (!dateTime) {
			return null
		}
		const safeDateTime = typeof dateTime == 'string' ?
			dateTime.trim() : ''
		const isTimeOnly = !safeDateTime.includes(' ') &&
			!safeDateTime.includes('T') &&
			safeDateTime.includes(':')
		const parsed = isTimeOnly ?
			parseIsoTime(dateTime) :zonedMoment(dateTime)
		const format = paramFormat || this.getTimeFormat()
		return parsed.format(format)
	},

	/**
	 * Formats a date and time for display for a user, e.g. Sep 4 1986 8:30 PM
	 */
	formatDateTime: function(dateTime, zone, paramTimeFormat, showTimeZone) {
		if (!dateTime) {
			return null
		}

		const timeFormat = paramTimeFormat || this.getTimeFormat()
		let format = `${dateFormat} ${timeFormat}`
		// include short time zone to format if needed
		if (showTimeZone) {
			format = format + ' z'
		}

		if (zone) {
			return getFormattedDateTime(dateTime, zone, format)
		}

		return zonedMoment(dateTime, zone).format(format)
	},

	/**
	 * Formats a date and time with time zone for display to the user,
	 * e.g. Sep 4 1986 8:30 PM EDT
	 */
	formatDateTimeWithZone(dateTime) {
		return this.formatDateTime(dateTime, defaultTimeZone(), null, true)
	},

	/**
	 * Returns just the date portion of a date, suitable for serializing to the
	 * server, e.g. 2015-12-25
	 *
	 * @param {Moment} date - a Moment instance representing the date
	 */
	isoDate: function(date) {
		if (!date) {
			return null
		}

		date = zonedMoment(date)

		// Don't try to format invalid dates; you can end up with something
		// like 'Nan-Nan-Nan'
		if (!date.isValid()) {
			return null
		}

		// #12188 don't call toISOString because it takes the time zone into
		// account which we don't want.  Also don't call .format() because it
		// returns Arabic instead of English

		var month = 1 + date.month() // months are 0-indexed
		if (month < 10) {
			month = '0' + month
		}
		var day = date.date()
		if (day < 10) {
			day = '0' + day
		}
		return date.year() + '-' + month + '-' + day
	},


	/**
	 * Given a local string from a browser, returns the locale string that will
	 * make moment happy.
	 */
	_locale: function(str) {
		var language = str.substr(0, 2)

		// handle taiwan
		if (str === 'zh-TW') {
			return 'zh-tw'
		}
		// handle other chinese
		if (language === 'zh') {
			return 'zh-cn'
		}

		// #13431 we need both language and country code
		return str.toLowerCase()
	},

	dateFormat,
	timeFormat,
	defaultTimeFormat,
	numericDateFormat,
	isoDateFormat,
	serverLocalDateTimeFormat,
	STRICT,
	NOT_STRICT,
	defaultTimeZone,
	parseIsoTime,
	getFormattedDateTime
}

//ie9 sets userLanguage, and other browsers set language
var navigatorLanguage = features.browser ?
						navigator.userLanguage || navigator.language : //production
						'EN-US' //for testing outside browser
var locale = momentHelper._locale(navigatorLanguage)

//change locale globally.  see http://momentjs.com/docs/#/i18n/
moment.locale(locale)

const timeFormat = defaultTimeFormat
const STRICT = true
const NOT_STRICT = false

export default momentHelper
momentHelper.__filename__ = 'components/moment-helper.js'
